import { useMemo } from "react";

import numeral from "numeral";

import { useTranslate } from "@similarweb/i18n-provider";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { ChangeCell } from "@similarweb/pro-logic-components";

import { LeaderValueCell } from "../../../common/widget/table/LeaderValueCell";
import { rowIndexColumn } from "../../../common/widget/table/rowIndexColumn";
import { useDomainColumn } from "../../../common/widget/table/useDomainColumn";

import type { ActivityAndAudienceRow } from "./types";

const GROUP_COLUMN_WIDTH = 140;
const AVG_SVI_COLUMN_WIDTH = 170;

export const useActivityAndAudienceColumns = (): ColumnsType<ActivityAndAudienceRow> => {
    const { translate } = useTranslate();
    const domainColumn = useDomainColumn({ dataAutomation: "social-activity-audience-domain-cell" });

    return useMemo(() => {
        const totalPostsColumn = {
            key: "totalPosts",
            title: translate("activation.alerts.social-media.metrics.activity-audience.column-title.total-posts"),
            children: [
                {
                    align: "right" as const,
                    dataIndex: "totalPosts" as const,
                    key: "totalPosts-volume",
                    title: translate("common.volume"),
                    width: GROUP_COLUMN_WIDTH,
                    render: (data: ActivityAndAudienceRow["totalPosts"]) => (
                        <LeaderValueCell
                            dataAutomation="social-activity-audience-total-posts-volume-cell"
                            isLeader={data.isLeader}
                            value={data.volume}
                        />
                    ),
                },
                {
                    align: "right" as const,
                    dataIndex: "totalPosts" as const,
                    key: "totalPosts-change",
                    title: translate("global.change"),
                    width: GROUP_COLUMN_WIDTH,
                    render: (data: ActivityAndAudienceRow["totalPosts"]) => (
                        <ChangeCell
                            dataAutomation="social-activity-audience-total-posts-change-cell"
                            formattedValue={
                                data.change != null ? numeral(Math.abs(data.change)).format("0.00%") : undefined
                            }
                            value={data.change ?? undefined}
                        />
                    ),
                },
            ],
        };

        const audienceSizeColumn = {
            key: "audienceSize",
            title: translate("activation.alerts.social-media.metrics.activity-audience.column-title.audience-size"),
            children: [
                {
                    align: "right" as const,
                    dataIndex: "audienceSize" as const,
                    key: "audienceSize-followers",
                    title: translate("activation.alerts.social-media.metrics.activity-audience.column-title.followers"),
                    width: GROUP_COLUMN_WIDTH,
                    render: (data: ActivityAndAudienceRow["audienceSize"]) => (
                        <LeaderValueCell
                            dataAutomation="social-activity-audience-followers-cell"
                            isLeader={data.isLeader}
                            value={data.followers}
                        />
                    ),
                },
                {
                    align: "right" as const,
                    dataIndex: "audienceSize" as const,
                    key: "audienceSize-change",
                    title: translate("global.change"),
                    width: GROUP_COLUMN_WIDTH,
                    render: (data: ActivityAndAudienceRow["audienceSize"]) => (
                        <ChangeCell
                            dataAutomation="social-activity-audience-audience-change-cell"
                            formattedValue={
                                data.change != null ? numeral(Math.abs(data.change)).format("0.00%") : undefined
                            }
                            value={data.change ?? undefined}
                        />
                    ),
                },
            ],
        };

        const avgSviColumn = {
            align: "center" as const,
            dataIndex: "avgSocialValueIndex" as const,
            key: "avgSocialValueIndex",
            title: translate("activation.alerts.social-media.metrics.activity-audience.column-title.avg-svi"),
            tooltipTitle: translate("activation.alerts.social-media.metrics.activity-audience.column-title.avg-svi"),
            tooltipSubtitle: translate("activation.alerts.social-media.metrics.activity-audience.tooltip.avg-svi"),
            width: AVG_SVI_COLUMN_WIDTH,
            render: (data: ActivityAndAudienceRow["avgSocialValueIndex"]) => (
                <LeaderValueCell
                    dataAutomation="social-activity-audience-avg-svi-cell"
                    isLeader={data.isLeader}
                    value={data.value}
                />
            ),
        };

        return [rowIndexColumn, domainColumn, totalPostsColumn, audienceSizeColumn, avgSviColumn];
    }, [domainColumn, translate]);
};